// PlanComponents/DifficultyCard.js
import React, { useState } from 'react';
import { Box, Paper, Typography, Button, CircularProgress, Tooltip, Fade, Collapse, Chip, Table, TableBody, TableRow, TableCell } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { bg, text as textColors, border, shadow, overlay } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';
import AdviceContentBox from '../../../components/AdviceContentBox';

// 難易度ごとのアイコンやカラーを定義したマップ
const difficultyIconMap = {
  "容易":         <CheckCircleIcon />,
  "やや容易":      <CheckIcon />,
  "やや困難":      <WarningIcon />,
  "困難":         <ReportProblemIcon />,
  "非常に困難":    <ErrorIcon />
};

// 難易度ごとのカラー（文字色 or バッジ色など）
const difficultyColorMap = {
  "容易":         "#4caf50",
  "やや容易":      "#8bc34a",
  "やや困難":      "#ffb74d",
  "困難":         "#f44336",
  "非常に困難":    "#d32f2f"
};

// 難易度のリスト（並び順）
const difficultyLevels = [
  "非常に困難",
  "困難",
  "やや困難",
  "やや容易",
  "容易"
];

// 影響度チップの色
const impactColorMap = {
  "高": "#f44336",
  "中": "#ff9800",
  "低": "#4caf50"
};

/**
 * 採用難易度カード
 */
export default function DifficultyCard({ difficulty, adv, onRegenerate, isRegenerating, successProbability }) {
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);

  // difficultyがオブジェクトの場合はlevelを取得、文字列の場合はそのまま使用（後方互換性）
  const difficultyLevel = typeof difficulty === 'object' && difficulty !== null
    ? difficulty.level
    : difficulty;

  // 難易度スコア（オブジェクトの場合のみ）
  const difficultyScore = typeof difficulty === 'object' && difficulty !== null
    ? difficulty.score
    : null;

  // 要因データ（バックエンド計算済み）
  const factors = typeof difficulty === 'object' && difficulty !== null
    ? difficulty.factors
    : null;

  // 市場比較コメント（バックエンド計算済み）
  const marketComparison = typeof difficulty === 'object' && difficulty !== null
    ? difficulty.marketComparison
    : null;

  const hasDetails = (factors && factors.length > 0) || marketComparison || successProbability;

  // 難易度を選択して再考する関数
  const handleRegenerateDifficulty = (newDifficulty) => {
    if (newDifficulty !== difficultyLevel && onRegenerate) {
      onRegenerate('difficulty', newDifficulty);
    }
  };

  return (
    <CardItem
      title="採用難易度"
      adviceContent={<AdviceContentBox adviceObj={adv.difficulty} />}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          backgroundColor: bg(theme).tinted,
          border: `1px solid ${border(theme).light}`,
          textAlign: 'center',
          color: textColors(theme).primary
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          {difficultyLevels.map((lvl) => {
            const icon  = difficultyIconMap[lvl]  || <HelpIcon />;
            const color = difficultyColorMap[lvl] || '#6a1b9a';

            const isSelected = (lvl === difficultyLevel);

            return (
              <Paper
                key={lvl}
                component={motion.div}
                whileHover={!isSelected ? { y: -2 } : {}}
                whileTap={!isSelected ? { scale: 0.98 } : {}}
                elevation={isSelected ? 4 : 1}
                sx={{
                  flex: 1,
                  py: 1.8,
                  px: 1,
                  borderRadius: 2,
                  border: isSelected ? `2px solid ${color}` : `1px solid ${border(theme).default}`,
                  backgroundColor: isSelected ? bg(theme).card : overlay(theme).whiteMedium,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.8,
                  cursor: !isSelected ? 'pointer' : 'default',
                  position: 'relative',
                  boxShadow: isSelected
                    ? '0 4px 12px rgba(0, 0, 0, 0.12)'
                    : '0 1px 3px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  '&:hover': !isSelected ? {
                    borderColor: color,
                    backgroundColor: bg(theme).card,
                    boxShadow: shadow(theme).card,
                  } : {}
                }}
                onClick={() => !isSelected && handleRegenerateDifficulty(lvl)}
              >
                <Box sx={{
                  color: isSelected ? color : 'rgba(0,0,0,0.4)',
                  transition: 'all 0.3s',
                }}>
                  {React.cloneElement(icon, {
                    fontSize: isSelected ? 'large' : 'medium',
                    sx: { transition: 'all 0.2s' }
                  })}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: isSelected ? color : textColors(theme).muted,
                    fontWeight: isSelected ? 'bold' : 'normal',
                    transition: 'all 0.3s',
                  }}
                >
                  {lvl}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!difficultyLevel ? (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              難易度：未設定
            </Typography>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: difficultyColorMap[difficultyLevel],
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {difficultyIconMap[difficultyLevel] && React.cloneElement(difficultyIconMap[difficultyLevel], { fontSize: 'small' })}
                現在の難易度：{difficultyLevel}
                {difficultyScore !== null && (
                  <span style={{ fontWeight: 'normal', fontSize: '0.85em', marginLeft: '8px' }}>
                    (スコア: {difficultyScore}/100)
                  </span>
                )}
              </Typography>
            </motion.div>
          )}

          {isRegenerating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#4e7df3' }}>
                再生成中...
              </Typography>
              <CircularProgress size={22} sx={{ color: '#4e7df3' }} />
            </Box>
          ) : difficultyLevel && (
            <Tooltip
              title="より容易な難易度で採用計画を再生成します"
              placement="top"
              arrow
              TransitionComponent={Fade}
            >
              <Button
                startIcon={<ArrowUpwardIcon />}
                variant="contained"
                size="small"
                sx={{
                  ml: 2,
                  borderRadius: 6,
                  px: 2,
                  py: 0.8,
                  backgroundColor: '#4caf50',
                  '&:hover': {
                    backgroundColor: '#43a047'
                  },
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => {
                  const currentIndex = difficultyLevels.indexOf(difficultyLevel);
                  const nextIndex = Math.min(currentIndex + 1, difficultyLevels.length - 1);
                  const nextDifficulty = difficultyLevels[nextIndex];
                  handleRegenerateDifficulty(nextDifficulty);
                }}
              >
                より容易に再考
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* 詳細展開トグル */}
        {hasDetails && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowDetails(!showDetails)}
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                color: textColors(theme).muted,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
              }}
            >
              {showDetails ? '詳細を閉じる' : '詳細を表示'}
            </Button>

            <Collapse in={showDetails}>
              <Box sx={{ mt: 1.5, textAlign: 'left' }}>
                {/* 要因テーブル */}
                {factors && factors.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).primary, mb: 1 }}>
                      難易度に影響する要因
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        {factors.map((f, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ border: 'none', py: 0.8, width: '30%' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).secondary }}>
                                {f.factor}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ border: 'none', py: 0.8, width: '25%' }}>
                              <Typography variant="caption" sx={{ color: textColors(theme).primary }}>
                                {f.value}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ border: 'none', py: 0.8, width: '15%' }}>
                              <Chip
                                label={f.impact}
                                size="small"
                                sx={{
                                  backgroundColor: impactColorMap[f.impact] || '#9e9e9e',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.65rem',
                                  height: 20
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ border: 'none', py: 0.8 }}>
                              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                                {f.benchmark}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* 市場比較コメント */}
                {marketComparison && (
                  <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).secondary, display: 'block', mb: 0.5 }}>
                      市場環境分析
                    </Typography>
                    <Typography variant="caption" sx={{ color: textColors(theme).primary }}>
                      {marketComparison}
                    </Typography>
                  </Box>
                )}

                {/* 成功確率 */}
                {successProbability && (
                  <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).secondary }}>
                        採用成功確率
                      </Typography>
                      <Chip
                        label={`${successProbability.probability}%`}
                        size="small"
                        sx={{
                          backgroundColor: successProbability.probability >= 60 ? '#4caf50'
                            : successProbability.probability >= 40 ? '#ff9800' : '#f44336',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                    {successProbability.factors && successProbability.factors.length > 0 && (
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>好条件:</Typography>
                        {successProbability.factors.map((f, i) => (
                          <Typography key={i} variant="caption" sx={{ color: textColors(theme).muted, display: 'block', ml: 1 }}>
                            ・{f}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {successProbability.risks && successProbability.risks.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 600 }}>リスク:</Typography>
                        {successProbability.risks.map((r, i) => (
                          <Typography key={i} variant="caption" sx={{ color: textColors(theme).muted, display: 'block', ml: 1 }}>
                            ・{r}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* 出典 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="構造化面接の予測妥当性 .51 vs 非構造化 .38（メタ分析研究に基づく）" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: textColors(theme).muted, cursor: 'help' }} />
                  </Tooltip>
                  <Typography variant="caption" sx={{ color: textColors(theme).muted, fontStyle: 'italic' }}>
                    ※ 有効求人倍率・面接妥当性は公的統計および学術研究に基づく
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </CardItem>
  );
}
